import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Eye, EyeOff, Check, Circle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  InputLabel,
  TextInput,
  ErrorText,
  PasswordInputWrapper,
  PasswordToggleButton,
  ModalCancelButton,
  ModalSubmitButton,
  PolicyList,
  PolicyItem,
} from './changePasswordModal.styles';
import { changePassword } from '../../../../services/auth';
import { checkPasswordPolicy, isPasswordPolicyMet } from '../../../../helper/passwordpolicyHelper';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const overlayRef = useRef(null);

  const policyResults = checkPasswordPolicy(newPassword);
  const policyMet = isPasswordPolicyMet(newPassword);
  const showPolicy = newPassword.length > 0; // shown while the user is typing
  const passwordsMatch = newPassword === confirmPassword;
  const showMismatch = confirmPassword.length > 0 && !passwordsMatch;

  const canSubmit =
    oldPassword.trim() !== '' && policyMet && confirmPassword !== '' && passwordsMatch;

  const mutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      window.alert('Password changed successfully');
      handleClose();
    },
    onError: (err) => {
      setErrors({ form: err?.message || 'Failed to change password. Please try again.' });
    },
  });

  const handleClose = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setErrors({});
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!oldPassword.trim()) nextErrors.oldPassword = 'Old password is required';
    if (!newPassword.trim()) nextErrors.newPassword = 'New password is required';
    else if (!policyMet) nextErrors.newPassword = 'New password does not meet the policy';
    if (!confirmPassword.trim()) nextErrors.confirmPassword = 'Confirm password is required';
    else if (!passwordsMatch) nextErrors.confirmPassword = 'Passwords do not match';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    mutation.mutate({ oldPassword, newPassword });
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay
      onClick={handleClose}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      ref={overlayRef}
    >
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle id="modal-title">Change password</ModalTitle>
          <ModalCloseButton onClick={handleClose} type="button" aria-label="Close">
            <X size={16} />
          </ModalCloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <InputLabel htmlFor="old-password">Old password</InputLabel>
              <PasswordInputWrapper>
                <TextInput
                  id="old-password"
                  type={showOldPassword ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="Enter current password"
                  style={{ paddingRight: '40px' }}
                  autoComplete="current-password"
                  $hasError={!!errors.oldPassword}
                  aria-describedby={errors.oldPassword ? 'old-password-error' : undefined}
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowOldPassword((v) => !v)}
                  aria-label={showOldPassword ? 'Hide password' : 'Show password'}
                >
                  {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </PasswordToggleButton>
              </PasswordInputWrapper>
              {errors.oldPassword && (
                <ErrorText id="old-password-error" role="alert">
                  {errors.oldPassword}
                </ErrorText>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="new-password">New password</InputLabel>
              <PasswordInputWrapper>
                <TextInput
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{ paddingRight: '40px' }}
                  autoComplete="new-password"
                  $hasError={!!errors.newPassword}
                  aria-describedby="password-policy new-password-error"
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </PasswordToggleButton>
              </PasswordInputWrapper>

              {showPolicy && (
                <PolicyList id="password-policy" aria-live="polite">
                  {policyResults.map((rule) => (
                    <PolicyItem key={rule.id} $passed={rule.passed}>
                      {rule.passed ? <Check size={12} /> : <Circle size={12} />}
                      <span>{rule.label}</span>
                    </PolicyItem>
                  ))}
                </PolicyList>
              )}

              {errors.newPassword && (
                <ErrorText id="new-password-error" role="alert">
                  {errors.newPassword}
                </ErrorText>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="confirm-password">Confirm password</InputLabel>
              <PasswordInputWrapper>
                <TextInput
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  style={{ paddingRight: '40px' }}
                  autoComplete="new-password"
                  $hasError={showMismatch || !!errors.confirmPassword}
                  aria-describedby={
                    showMismatch || errors.confirmPassword ? 'confirm-password-error' : undefined
                  }
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </PasswordToggleButton>
              </PasswordInputWrapper>
              {(showMismatch || errors.confirmPassword) && (
                <ErrorText id="confirm-password-error" role="alert">
                  {showMismatch ? 'Passwords do not match' : errors.confirmPassword}
                </ErrorText>
              )}
            </FormGroup>

            {errors.form && <ErrorText role="alert">{errors.form}</ErrorText>}
          </ModalBody>

          <ModalFooter>
            <ModalCancelButton type="button" onClick={handleClose}>
              Cancel
            </ModalCancelButton>
            <ModalSubmitButton type="submit" disabled={mutation.isPending || !canSubmit}>
              {mutation.isPending ? (
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              ) : null}
              {mutation.isPending ? 'Updating…' : 'Update password'}
            </ModalSubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default ChangePasswordModal;
